import { Link } from 'react-router-dom';
import { getSeoContent, getInternalLinks, getRoomsContent } from '../lib/seoContent';
import { buildListPath } from '../lib/urls';
import { useTr } from '../lib/i18n';
import { RU_BASE_MAP, getRuContent, ruListPath, ruCityLoc } from '../lib/ru';

// SEO text blocks for listing pages:
//  part="intro" — 50-80 word paragraph rendered right after the H1
//  part="full"  — long text (h2 + paragraphs) + internal links + CTA,
//                 rendered after the listings
const SeoText = ({ part, base, saleType, loc, locationSlug, sectorSlug, roomsKey }) => {
    const tr = useTr();
    const lang = tr('ro', 'ru');

    let content;
    if (lang === 'ru') {
        const ruBase = RU_BASE_MAP[base] || base;
        content = getRuContent(ruBase, saleType, ruCityLoc(locationSlug) || loc);
    } else {
        content = roomsKey ? getRoomsContent(roomsKey, loc) : getSeoContent(base, saleType, loc);
    }
    if (!content) return null;

    if (part === 'intro') {
        return (
            <p className='text-[16px] font-body text-gray-700 leading-[1.6] max-w-[900px]'>
                {content.intro}
            </p>
        );
    }

    // Internal links: same category with the opposite sale type + two related
    // categories (RU versions link the RU pages).
    let links;
    if (lang === 'ru') {
        const ruBase = RU_BASE_MAP[base] || base;
        const other = Number(saleType) === 2 ? 1 : 2;
        const sameLabel = ruBase === 'kvartiry' ? 'Квартиры' : ruBase === 'doma' ? 'Дома'
            : ruBase === 'uchastki' ? 'Участки' : 'Коммерческая недвижимость';
        const others = {
            kvartiry: [['doma', 'Дома'], ['uchastki', 'Участки']],
            doma: [['kvartiry', 'Квартиры'], ['uchastki', 'Участки']],
            uchastki: [['doma', 'Дома'], ['kvartiry', 'Квартиры']],
            'kommercheskaya-nedvizhimost': [['kvartiry', 'Квартиры'], ['doma', 'Дома']],
        }[ruBase] || [];
        const action = Number(saleType) === 2 ? 'в аренду' : 'на продажу';
        const otherAction = Number(saleType) === 2 ? 'на продажу' : 'в аренду';
        links = [
            { label: `${sameLabel} ${otherAction} в ${ruCityLoc(locationSlug) || loc}`, to: ruListPath(ruBase, locationSlug || 'ungheni', other) },
            ...others.map(([ru, label]) => ({
                label: `${label} ${action} в ${ruCityLoc(locationSlug) || loc}`,
                to: ruListPath(ru, locationSlug || 'ungheni', Number(saleType)),
            })),
        ];
    } else {
        links = getInternalLinks(base, saleType, loc).map((link) => ({
            label: link.label,
            to: buildListPath(link.base, { location: locationSlug, sector: sectorSlug, saleType: link.saleType }),
        }));
    }

    return (
        <div className="flex flex-col gap-[15px] pt-[10px]">
            <h2 className='text-[18px] font-poppins font-[500] sm:text-[20px]'>{content.h2}</h2>
            {content.paragraphs.map((paragraph, idx) => (
                <p key={idx} className='text-[15px] font-body text-gray-700 leading-[1.65]'>
                    {paragraph}
                </p>
            ))}

            <div className="flex flex-wrap gap-[8px] pt-[5px] text-[14px] font-body">
                {links.map((link, idx) => (
                    <Link
                        key={idx}
                        to={link.to}
                        className="bg-white border-[1px] border-gray-300 rounded-[5px] px-[14px] py-[7px] text-[#181616] hover:border-[#d5ab63] hover:text-[#b48a4d] transition-all duration-100 ease-in"
                    >
                        {link.label}
                    </Link>
                ))}
            </div>

            <div className="mt-[10px] bg-[#ececec] rounded-[5px] p-[25px] flex flex-col gap-[10px]">
                <h2 className='text-[18px] font-poppins font-[500] sm:text-[20px]'>
                    {tr('Nu ai găsit proprietatea potrivită?', 'Не нашли подходящий объект?')}
                </h2>
                <p className='text-[15px] font-body text-gray-700 leading-[1.6]'>
                    {tr(
                        'Spune-ne ce cauți, zona și bugetul disponibil. Te ajutăm să identifici ofertele care corespund criteriilor tale.',
                        'Расскажите, что вы ищете, в каком районе и с каким бюджетом. Мы поможем подобрать подходящие предложения.'
                    )}
                </p>
                <Link
                    to={lang === 'ru' ? '/ru/contacte' : '/contacte'}
                    className='btn-main text-white text-[14px] px-[30px] py-[8px] rounded-[5px] w-fit'
                >
                    {tr('Trimite o cerere', 'Оставить заявку')}
                </Link>
            </div>
        </div>
    );
};

export default SeoText;
